import { forwardRef, useMemo, useRef } from 'react'
import Menu, { MenuProps } from '@mui/material/Menu'
import useForkRef from '@mui/utils/useForkRef'
import { useCloseOnPointerDownOutside, useToggleCloseOnAnchorClick } from '../../hooks/useCloseOnPointerDownOutside'

/** Portaled dialogs (e.g. ReusableDialog) are outside the menu paper; ignore them so confirm actions still run. */
const DEFAULT_APP_MENU_IGNORE_SELECTORS = ['.MuiDialog-root']

export type AppMenuProps = MenuProps & {
    /** Keep open while interacting with these elements (e.g. anchor button). */
    clickAwayIgnoreRefs?: React.RefObject<HTMLElement | null>[]
    /** Keep open while the event target is inside these selectors. */
    clickAwayIgnoreSelectors?: string[]
    /** When false, a second click on the anchor/trigger does not close the menu. Default: true. */
    toggleOnAnchorClick?: boolean
}

/**
 * Non-modal menu (hideBackdrop + pointer-events passthrough).
 * Closes on outside pointer down and on second click of the anchor/trigger button.
 */
export const AppMenu = forwardRef<HTMLDivElement, AppMenuProps>(function AppMenu(
    {
        open,
        onClose,
        anchorEl,
        clickAwayIgnoreRefs,
        clickAwayIgnoreSelectors,
        toggleOnAnchorClick = true,
        slotProps,
        sx,
        PaperProps,
        children,
        ...menuProps
    },
    ref
) {
    const menuPaperRef = useRef<HTMLDivElement>(null)

    const ignoreRefs = useMemo(() => {
        const refs = [...(clickAwayIgnoreRefs ?? [])]
        if (anchorEl) {
            refs.push({ current: anchorEl })
        }
        return refs
    }, [anchorEl, clickAwayIgnoreRefs])

    const ignoreSelectors = useMemo(
        () => [...DEFAULT_APP_MENU_IGNORE_SELECTORS, ...(clickAwayIgnoreSelectors ?? [])],
        [clickAwayIgnoreSelectors]
    )

    useCloseOnPointerDownOutside(Boolean(open), () => onClose?.({}, 'backdropClick'), {
        containerRef: menuPaperRef,
        ignoreRefs,
        ignoreSelectors
    })

    useToggleCloseOnAnchorClick(Boolean(open), onClose, ignoreRefs, !toggleOnAnchorClick)

    const paperSlot = slotProps?.paper
    const paperSlotRef =
        paperSlot && typeof paperSlot === 'object' && 'ref' in paperSlot
            ? (paperSlot as { ref?: React.Ref<HTMLDivElement> }).ref
            : undefined
    const mergedPaperRef = useForkRef(menuPaperRef, paperSlotRef)

    const mergedSlotProps = {
        ...slotProps,
        paper: {
            ...(typeof paperSlot === 'object' ? paperSlot : {}),
            ...PaperProps,
            ref: mergedPaperRef,
            sx: [
                { pointerEvents: 'auto' },
                ...(typeof paperSlot === 'object' && paperSlot?.sx
                    ? Array.isArray(paperSlot.sx)
                        ? paperSlot.sx
                        : [paperSlot.sx]
                    : []),
                ...(PaperProps?.sx
                    ? Array.isArray(PaperProps.sx)
                        ? PaperProps.sx
                        : [PaperProps.sx]
                    : [])
            ]
        }
    }

    return (
        <Menu
            ref={ref}
            open={open}
            onClose={onClose}
            anchorEl={anchorEl}
            hideBackdrop
            disableScrollLock
            disableEnforceFocus
            disableAutoFocus
            disableRestoreFocus
            sx={[{ pointerEvents: 'none' }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
            slotProps={mergedSlotProps}
            {...menuProps}
        >
            {children}
        </Menu>
    )
})
